import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '../AuthContext';
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Trash2, ArrowRight, X, Printer, Store, ShoppingBag, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  barcode: 'Barcode / UPC',
  item_number: "Item Number (Sam's)",
  product_name: 'Product Name *',
  retail_price: 'Retail Price',
  quantity: 'Quantity',
  cost: 'Cost',
};

export default function BulkUpload() {
  const [step, setStep] = useState(1); // 1=upload, 2=map, 3=review
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [costPercent, setCostPercent] = useState(28);
  const [costBasis, setCostBasis] = useState('auto'); // auto | per_item | line_total
  const [retailBasis, setRetailBasis] = useState('auto');
  const [batchId, setBatchId] = useState('');
  const [batches, setBatches] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [skuMsg, setSkuMsg] = useState('');
  const [committedCount, setCommittedCount] = useState(null);
  const [error, setError] = useState('');
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [printTemplateId, setPrintTemplateId] = useState('');
  // Optional secondary Shopify collection applied to every row in this batch.
  // Synced live from the user's Shopify store via /api/shopify/collections.
  const [shopifyCollections, setShopifyCollections] = useState([]);
  const [secondaryCollection, setSecondaryCollection] = useState('');
  const [loadingShopifyColl, setLoadingShopifyColl] = useState(false);
  // Default for "Shopify Physical product" toggle when pushing every row in
  // this batch. Per-row override is supported via it.requiresShipping.
  const [batchRequiresShipping, setBatchRequiresShipping] = useState(true);
  // Batch default for "Local Pickup Only" (physical + taxable, checkout = pickup only).
  // Per-row override via it.localPickupOnly (MapPin toggle in the Actions column).
  const [batchLocalPickup, setBatchLocalPickup] = useState(false);
  // Which Shopify sales channels bulk pushes publish to: 'all' | 'pos'.
  const [batchChannelMode, setBatchChannelMode] = useState('all');
  const [labelPreview, setLabelPreview] = useState(null); // { image, product_name }
  const fileRef = useRef(null);

  useEffect(() => {
    apiCall('/api/batches', { method: 'GET' }).then(r => setBatches(r.data.batches || [])).catch(() => {});
    // Fetch the merchant's existing Shopify collections for the secondary-collection picker.
    setLoadingShopifyColl(true);
    apiCall('/api/shopify/collections', { method: 'GET' })
      .then(r => setShopifyCollections(r.data?.collections || []))
      .catch(() => setShopifyCollections([]))
      .finally(() => setLoadingShopifyColl(false));
    apiCall('/api/label-templates', { method: 'GET' }).then(r => {
      const list = r.data || [];
      setLabelTemplates(list);
      // Default to 2x1 if available
      const two = list.find(t => (t.size || '').toLowerCase() === '2x1');
      if (two) setPrintTemplateId(two.id);
      else if (list[0]) setPrintTemplateId(list[0].id);
    }).catch(() => {});
  }, []);

  const uploadFile = async (f) => {
    setError(''); setParsing(true);
    try {
      let token = '';
      try {
        const stored = localStorage.getItem('xlabel_user');
        if (stored) token = JSON.parse(stored).access_token || '';
      } catch {}
      if (!token) {
        token = document.cookie.match(/access_token=([^;]+)/)?.[1] || '';
      }
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API}/api/bulk-upload/parse`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      setParseResult(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
    } catch (e) { setError(e.message); } finally { setParsing(false); }
  };

  const processRows = async () => {
    if (!mapping.product_name || (!mapping.barcode && !mapping.item_number)) {
      setError('Please map Product Name and at least one of Barcode or Item Number before continuing.');
      return;
    }
    setError(''); setProcessing(true); setEnrichProgress(null);
    try {
      const res = await apiCall('/api/bulk-upload/process', {
        method: 'POST',
        data: { session_id: parseResult.session_id, mapping, batch_id: batchId, cost_percent: Number(costPercent) || 28, cost_basis: costBasis, retail_basis: retailBasis },
      });

      // Small manifest: got items back synchronously
      if (!res.data.async) {
        const list = res.data.items || [];
        setItems(list);
        setSelected(Object.fromEntries(list.map(it => [it.row_id, true])));
        setSkuMsg(res.data.sku_limited_msg || '');
        setStep(3);
        autoSaveAll(list); // persist every parsed row to Products
        return;
      }

      // Large manifest: backend processes in background; move to step 3 immediately and poll
      setItems([]); setSelected({}); setSkuMsg(res.data.sku_limited_msg || '');
      setStep(3);
      setEnrichProgress({ done: 0, total: res.data.total || 0, status: 'running' });

      const sid = parseResult.session_id;
      const poll = async () => {
        try {
          const s = await apiCall(`/api/bulk-upload/status/${sid}`, { method: 'GET' });
          const d = s.data || {};
          setEnrichProgress({
            done: d.progress?.done || 0,
            total: d.progress?.total || 0,
            status: d.status,
          });
          if (Array.isArray(d.items)) {
            setItems(d.items);
            setSelected(prev => {
              // Auto-select new rows that weren't tracked yet
              const next = { ...prev };
              d.items.forEach(it => { if (next[it.row_id] === undefined) next[it.row_id] = true; });
              return next;
            });
            autoSaveAll(d.items); // persist newly-streamed rows to Products
          }
          if (d.status === 'done') { setEnrichProgress(p => p ? { ...p, status: 'done' } : p); return; }
          if (d.status === 'error') { setError('Enrichment failed. Try a smaller file.'); return; }
          setTimeout(poll, 2000);
        } catch (_e) {
          // Brief transient failures shouldn't kill the poll loop
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 1200);
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setProcessing(false); }
  };

  const commit = async () => {
    setError(''); setCommitting(true);
    try {
      // Rows are auto-saved to Products as they're reviewed — just make sure
      // any stragglers are persisted, then show the summary. No duplicate insert.
      await autoSaveAll(itemsRef.current.length ? itemsRef.current : items);
      setCommittedCount(Object.keys(savedRowMap.current).length);
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setCommitting(false); }
  };

  const updateItem = (row_id, field, value) => {
    setItems(prev => prev.map(it => it.row_id === row_id ? { ...it, [field]: value } : it));
  };

  // Bulk pricing: apply a pricing rule to all selected rows
  const [priceMode, setPriceMode] = useState('markup_cost'); // markup_cost | discount_retail | flat_margin
  const [pricePct, setPricePct] = useState(30);
  const applyBulkPricing = () => {
    const pct = Number(pricePct) || 0;
    setItems(prev => prev.map(it => {
      if (!selected[it.row_id]) return it;
      const retail = Number(it.retail_price) || 0;
      const cost = Number(it.cost) || 0;
      let final_price = Number(it.final_price) || 0;
      if (priceMode === 'markup_cost') {
        // FINAL = COST * (1 + pct/100)
        final_price = cost * (1 + pct / 100);
      } else if (priceMode === 'discount_retail') {
        // FINAL = RETAIL * (1 - pct/100)
        final_price = retail * (1 - pct / 100);
      } else if (priceMode === 'flat_margin') {
        // FINAL = COST / (1 - pct/100)  → guarantees `pct%` margin on sale price
        if (pct >= 100) final_price = cost * 10; // safety
        else final_price = cost / (1 - pct / 100);
      }
      return { ...it, final_price: Math.round(final_price * 100) / 100 };
    }));
  };

  const setRowPctFrom = (row_id, mode, pctVal) => {
    setItems(prev => prev.map(it => {
      if (it.row_id !== row_id) return it;
      const retail = Number(it.retail_price) || 0;
      const cost = Number(it.cost) || 0;
      const pct = Number(pctVal) || 0;
      let final_price = Number(it.final_price) || 0;
      if (mode === 'retail') final_price = retail * (1 - pct / 100);
      else if (mode === 'cost') final_price = cost * (1 + pct / 100);
      return { ...it, final_price: Math.round(final_price * 100) / 100 };
    }));
  };

  const [rowAction, setRowAction] = useState({}); // {row_id: 'saving'|'print'|'shopify'|'square'|'ebay'}
  const [rowStatus, setRowStatus] = useState({}); // {row_id: {ok:true} | {ok:false, reason:'...'}}
  const [bulkSummary, setBulkSummary] = useState(null); // {total, ok, failed:[{name,reason}], needsReconnect:bool}
  const [enrichProgress, setEnrichProgress] = useState(null); // {done, total, status}

  // Save a single row as a product, return the saved product_id (or null if duplicate-safe skip not needed)
  const _saveRowAsProduct = async (it) => {
    const payload = {
      batch_id: batchId || it.batch_id || '',
      product_name: it.product_name || 'Unnamed',
      product_desc: it.product_desc || '',
      barcode: it.barcode || '',
      // Manifest's Item Number column → persisted on the product so it shows
      // in the Products list and gets reused as SKU on future pushes.
      item_number: it.item_number || '',
      quantity: Number(it.quantity) || 1,
      selling_price: Number(it.final_price) || 0,
      retail_price: String(it.retail_price || ''),
      // Persist any images parsed/lifted from the manifest so they show on the
      // Products list, the Quick Edit screen, and survive a re-push later.
      images: Array.isArray(it.images) ? it.images : [],
      print_label: false, push_shopify: false, push_square: false,
    };
    const res = await apiCall('/api/products', { method: 'POST', data: payload });
    return res.data?.product?.product_id || null;
  };

  const _finishRow = (row_id) => {
    setRowAction(prev => { const n = { ...prev }; delete n[row_id]; return n; });
    setSelected(prev => ({ ...prev, [row_id]: false }));
  };

  // ── AUTO-SAVE every parsed row to Products (no Print/Push required) ──────
  const savedRowMap = useRef({}); // row_id -> product_id (dedupe + edit-sync)
  const itemsRef = useRef([]);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const [autoSave, setAutoSave] = useState({ total: 0, saved: 0, running: false });

  // Save a row once; reuse the product_id on later actions/edits (no duplicates).
  const ensureRowSaved = async (it) => {
    if (savedRowMap.current[it.row_id]) return savedRowMap.current[it.row_id];
    const pid = await _saveRowAsProduct(it);
    if (pid) savedRowMap.current[it.row_id] = pid;
    return pid;
  };

  // Persist all not-yet-saved rows in small parallel batches as they stream in.
  const autoSaveAll = async (rows) => {
    const pending = (rows || []).filter(it => it.product_name && !savedRowMap.current[it.row_id]);
    if (pending.length === 0) return;
    setAutoSave(() => ({ total: Object.keys(savedRowMap.current).length + pending.length, saved: Object.keys(savedRowMap.current).length, running: true }));
    const CHUNK = 5;
    for (let i = 0; i < pending.length; i += CHUNK) {
      const slice = pending.slice(i, i + CHUNK);
      await Promise.all(slice.map(it => ensureRowSaved(it).catch(() => {})));
      setAutoSave(s => ({ ...s, saved: Object.keys(savedRowMap.current).length }));
    }
    setAutoSave(s => ({ ...s, running: false }));
  };

  // When a row's editable fields change (on blur), update its saved product.
  const syncRowEdit = async (row_id) => {
    const pid = savedRowMap.current[row_id];
    if (!pid) return;
    const it = (itemsRef.current || []).find(x => x.row_id === row_id);
    if (!it) return;
    try {
      await apiCall(`/api/products/${pid}`, { method: 'PUT', data: {
        product_name: it.product_name || 'Unnamed',
        quantity: Number(it.quantity) || 1,
        selling_price: Number(it.final_price) || 0,
        retail_price: String(it.retail_price || ''),
      } });
    } catch (_e) { /* silent — surfaced on next explicit action if it matters */ }
  };

  // Mark a row's saved product as pushed to a channel so it counts toward the
  // batch total (only pushed products contribute to batch selling/profit).
  const markRowPushed = async (row_id, channel) => {
    const pid = savedRowMap.current[row_id];
    if (!pid) return;
    const flag = channel === 'shopify' ? { push_shopify: true } : channel === 'square' ? { push_square: true } : channel === 'ebay' ? { push_ebay: true } : null;
    if (!flag) return;
    try { await apiCall(`/api/products/${pid}`, { method: 'PUT', data: flag }); } catch (_e) {}
  };

  // Dropping a row also removes its auto-saved product.
  const dropRow = async (row_id) => {
    const pid = savedRowMap.current[row_id];
    setItems(prev => prev.filter(r => r.row_id !== row_id));
    if (pid) {
      delete savedRowMap.current[row_id];
      try { await apiCall(`/api/products/${pid}`, { method: 'DELETE' }); } catch (_e) {}
    }
  };

  // ── BULK ACTIONS ────────────────────────────────────────
  const [bulkBusy, setBulkBusy] = useState(null); // null | 'shopify' | 'square' | 'print'
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [marginMin, setMarginMin] = useState(15);
  const [marginMax, setMarginMax] = useState(25);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Unique categories observed in the enriched rows (for the dropdown)
  const availableCategories = React.useMemo(() => {
    const s = new Set();
    items.forEach(it => { if (it.category) s.add(it.category); });
    return Array.from(s).sort();
  }, [items]);

  const selectByMargin = () => {
    const lo = Number(marginMin);
    const hi = Number(marginMax);
    const min = isNaN(lo) ? 0 : lo;
    const max = isNaN(hi) ? 1000 : hi;
    const cat = (categoryFilter || '').trim().toLowerCase();
    const next = {};
    items.forEach(it => {
      const retail = Number(it.retail_price) || 0;
      const cost = Number(it.cost) || 0;
      // Cost % of retail (what you actually paid) — e.g. 18% means "bought at 18% of retail"
      const costPct = retail > 0 ? (cost / retail) * 100 : 0;
      const okCost = costPct >= min && costPct <= max;
      const okCat = cat ? (String(it.category || '').toLowerCase().includes(cat)) : true;
      if (okCost && okCat) next[it.row_id] = true;
    });
    setSelected(next);
  };

  const bulkPush = async (platform /* 'shopify' | 'square' */) => {
    const chosen = items.filter(it => selected[it.row_id]);
    if (chosen.length === 0) { setError('Select at least one item.'); return; }
    setBulkBusy(platform); setError('');
    setBulkSummary(null);
    // Clear previous statuses for the chosen rows
    setRowStatus(prev => { const n = { ...prev }; chosen.forEach(it => { delete n[it.row_id]; }); return n; });
    setBulkProgress({ done: 0, total: chosen.length });
    const failed = [];
    let ok = 0;
    let needsReconnect = false;
    for (let i = 0; i < chosen.length; i++) {
      const it = chosen[i];
      let result;
      try {
        if (platform === 'shopify') result = await quickPushShopify(it);
        else if (platform === 'square') result = await quickPushSquare(it);
      } catch (e) {
        result = { ok: false, reason: e.response?.data?.detail || e.message || 'unknown error' };
      }
      if (result?.ok) {
        ok += 1;
        setRowStatus(p => ({ ...p, [it.row_id]: { ok: true } }));
      } else {
        failed.push({ name: it.product_name || it.barcode || 'Unnamed', reason: result?.reason || 'unknown' });
        setRowStatus(p => ({ ...p, [it.row_id]: { ok: false, reason: result?.reason || 'unknown' } }));
        if (result?.needsReconnect) {
          needsReconnect = true;
          // All subsequent pushes will hit the same scope issue — stop early to save API calls
          setBulkProgress({ done: i + 1, total: chosen.length });
          break;
        }
      }
      setBulkProgress(p => ({ ...p, done: i + 1 }));
    }
    setBulkBusy(null);
    setBulkSummary({ total: chosen.length, ok, failed, needsReconnect, platform });
  };

  const bulkPrint = async () => {
    const chosen = items.filter(it => selected[it.row_id]);
    if (chosen.length === 0) { setError('Select at least one item.'); return; }
    setBulkBusy('print'); setError('');
    const totalTags = chosen.reduce((s, it) => s + Math.max(1, Number(it.quantity) || 1), 0);
    setBulkProgress({ done: 0, total: totalTags });
    try {
      const r = await apiCall('/api/print-labels-bulk', {
        method: 'POST',
        data: {
          template_id: printTemplateId,
          items: chosen.map(it => ({
            product_name: it.product_name,
            retail_price: String(it.retail_price || 0),
            final_price: String(it.final_price || 0),
            barcode: it.barcode,
            quantity: Number(it.quantity) || 1,
          })),
        },
      });
      const labels = r.data?.labels || [];
      const generated = r.data?.generated || labels.length;
      const failed = r.data?.failed || [];
      const expected = r.data?.expected || totalTags;
      const size = r.data?.size || '2x1';
      setBulkProgress({ done: generated, total: expected });
      // Feed all labels into a single hidden print iframe so every tag prints as its own page
      openBulkPrintWindow(labels, size);
      // Do NOT uncheck rows — user prefers to keep selection until manually cleared.
      if (failed.length > 0) {
        setError(`Printed ${generated} of ${expected} tags. ${failed.length} item(s) failed: ${failed.slice(0, 3).map(f => `${f.barcode || '?'} (${f.reason || 'unknown'})`).join(' · ')}${failed.length > 3 ? ` (+${failed.length - 3} more)` : ''}`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Bulk print failed');
    } finally {
      setBulkBusy(null);
    }
  };

  // Print N labels via a hidden iframe. Each label = one page using the template's @page size.
  const openBulkPrintWindow = (labels, sizeStr) => {
    if (!labels || labels.length === 0) return;
    try {
      const prev = document.getElementById('__bulk_print_iframe');
      if (prev) prev.remove();
      const sizeMap = { '2x1': '2in 1in', '3x2': '3in 2in', '4x6': '4in 6in' };
      const pageSize = sizeMap[(sizeStr || '2x1').toLowerCase()] || '2in 1in';
      const iframe = document.createElement('iframe');
      iframe.id = '__bulk_print_iframe';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      const imgs = labels.map((l, i) => `<div class="pg"><img src="${l.image}" alt="label ${i + 1}" /></div>`).join('');
      doc.open();
      doc.write(`<!DOCTYPE html><html><head><title>Bulk Labels</title>
<style>
  @page { size: ${pageSize}; margin: 0; }
  html, body { margin: 0; padding: 0; }
  .pg { width: 100vw; height: 100vh; page-break-after: always; display: flex; align-items: center; justify-content: center; }
  .pg:last-child { page-break-after: auto; }
  img { width: 100%; height: 100%; object-fit: contain; display: block; }
</style></head><body>${imgs}</body></html>`);
      doc.close();

      const doPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error(e); }
      };
      // Wait for last image to load then print
      const last = doc.images[doc.images.length - 1];
      if (last && last.complete && last.naturalWidth > 0) setTimeout(doPrint, 500);
      else if (last) last.addEventListener('load', () => setTimeout(doPrint, 500));
      else setTimeout(doPrint, 800);
      setTimeout(() => { try { iframe.remove(); } catch {} }, 30000);
    } catch (e) {
      setError('Could not open print dialog. Try again or use the per-row print button.');
    }
  };

  const quickPrint = async (it) => {
    setError(''); setRowAction(p => ({ ...p, [it.row_id]: 'print' }));
    try {
      await ensureRowSaved(it);
      const res = await apiCall('/api/print-label', {
        method: 'POST',
        data: {
          product_name: it.product_name, retail_price: String(it.retail_price || '0'),
          our_price: String(it.final_price || '0'), barcode: it.barcode,
          template_id: printTemplateId,
        },
      });
      if (res.data?.error) throw new Error(res.data.message || 'Print failed');
      // Resolve the size label (e.g. "2x1") from the selected template
      const tpl = labelTemplates.find(t => t.id === printTemplateId);
      const size = tpl?.size || '2x1';
      // Show label in an in-page modal — reliable print via user click (no popup blockers)
      setLabelPreview({ image: res.data.image, product_name: it.product_name, row_id: it.row_id, size });
      _finishRow(it.row_id);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setRowAction(p => { const n = { ...p }; delete n[it.row_id]; return n; });
    }
  };

  const quickPushSquare = async (it) => {
    setError(''); setRowAction(p => ({ ...p, [it.row_id]: 'square' }));
    try {
      await ensureRowSaved(it);
      const hasImg = !!(it.images && it.images[0]);
      await apiCall('/api/push-square', {
        method: 'POST',
        data: {
          barCode: it.barcode, productName: it.product_name, Desc: it.product_desc || '',
          finalPrice: String(it.final_price || 0), retailPrice: String(it.retail_price || 0),
          quantity: String(it.quantity || 1),
          squareImageUrl: hasImg ? [it.images[0]] : [],
          availableOnline: hasImg, // no image → POS only
          // SKU comes from the manifest's Item Number column. Fall back to barcode
          // when the manifest doesn't have a separate item number.
          sku: it.item_number || it.barcode,
          // Manifest category → Square will resolve/create a CATEGORY object by name
          categoryName: it.category || '',
        },
      });
      await markRowPushed(it.row_id, 'square');
      _finishRow(it.row_id);
      setRowStatus(p => ({ ...p, [it.row_id]: { ok: true } }));
      return { ok: true };
    } catch (e) {
      const reason = e.response?.data?.detail || e.message || 'Square push failed';
      setError(reason);
      setRowAction(p => { const n = { ...p }; delete n[it.row_id]; return n; });
      setRowStatus(p => ({ ...p, [it.row_id]: { ok: false, reason } }));
      return { ok: false, reason };
    }
  };

  const quickPushShopify = async (it) => {
    setError(''); setRowAction(p => ({ ...p, [it.row_id]: 'shopify' }));
    try {
      await ensureRowSaved(it);
      const hasImg = !!(it.images && it.images[0]);
      const r = await apiCall('/api/push-shopify', {
        method: 'POST',
        data: {
          barCode: it.barcode, productName: it.product_name, Desc: it.product_desc || '',
          finalPrice: String(it.final_price || 0),
          // Retail price doubles as compareAtPrice so Shopify shows the savings ribbon
          compareAtPrice: Number(it.retail_price) > Number(it.final_price) ? String(it.retail_price) : '',
          cost: String(it.cost || 0),
          quantity: String(it.quantity || 1),
          // SKU comes from the manifest's Item Number column. Fall back to barcode
          // when the manifest doesn't have a separate item number.
          sku: it.item_number || it.barcode,
          squareImageUrl: hasImg ? [it.images[0]] : [],
          // Manifest category → goes straight into Shopify's productType (free text).
          // Reviewers/merchants see this exact label on the storefront.
          productType: it.category || '',
          // Per-row override (it.secondaryCollection) wins; otherwise the
          // batch-level secondaryCollection picker applies to every row.
          additionalCollections: [it.secondaryCollection || secondaryCollection].filter(Boolean),
          // Shopify "Physical product" toggle (defaults to batch-level value).
          requiresShipping: typeof it.requiresShipping === 'boolean' ? it.requiresShipping : batchRequiresShipping,
          // Local Pickup Only: per-row toggle wins, else batch-level default.
          localPickupOnly: typeof it.localPickupOnly === 'boolean' ? it.localPickupOnly : batchLocalPickup,
          // Channel mode: 'pos' → publish to Point of Sale only; 'all' → backend
          // publishes to every sales channel on the store.
          salesChannels: batchChannelMode === 'pos' ? ['point of sale'] : [],
        },
      });
      const diag = r.data?.publish_diagnostic;
      if (diag) {
        const pubs = diag.published_to || [];
        const found = diag.publications_found || [];
        if (pubs.length === 0) {
          let reason;
          let needsReconnect = false;
          if (diag.publications_denied) {
            reason = 'missing read_publications scope — reconnect Shopify';
            needsReconnect = true;
          } else if (!found.some(n => (n || '').toLowerCase().includes('pos') || (n || '').toLowerCase().includes('point of sale'))) {
            reason = `no POS channel (found: ${found.join(', ') || 'none'})`;
          } else {
            reason = `publish to POS failed: ${(diag.errors || []).join('; ') || 'unknown'}`;
          }
          setRowAction(p => { const n = { ...p }; delete n[it.row_id]; return n; });
          setRowStatus(p => ({ ...p, [it.row_id]: { ok: false, reason } }));
          return { ok: false, reason, needsReconnect };
        }
      }
      const lp = r.data?.local_pickup;
      await markRowPushed(it.row_id, 'shopify');
      _finishRow(it.row_id);
      setRowStatus(p => ({ ...p, [it.row_id]: (lp && !lp.assigned) ? { ok: true, warn: `pickup-only not applied: ${lp.error}` } : { ok: true } }));
      return { ok: true };
    } catch (e) {
      const reason = e.response?.data?.detail || e.message || 'Shopify push failed';
      setError(reason);
      setRowAction(p => { const n = { ...p }; delete n[it.row_id]; return n; });
      setRowStatus(p => ({ ...p, [it.row_id]: { ok: false, reason } }));
      return { ok: false, reason };
    }
  };

  // Prints an image via a hidden same-origin iframe (most reliable method across all browsers)
  // sizeStr: '2x1' | '3x2' | '4x6' — controls @page size so the label prints at its true dimensions
  const openPrintWindow = (imageDataUrl, title, sizeStr) => {
    try {
      // Remove previous print iframe if any
      const prev = document.getElementById('__bulk_print_iframe');
      if (prev) prev.remove();

      // Map template size → @page dimensions (width x height in inches)
      const sizeMap = { '2x1': '2in 1in', '3x2': '3in 2in', '4x6': '4in 6in' };
      const pageSize = sizeMap[(sizeStr || '2x1').toLowerCase()] || '2in 1in';

      const iframe = document.createElement('iframe');
      iframe.id = '__bulk_print_iframe';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`<!DOCTYPE html><html><head><title>${title || 'Label'}</title>
<style>
  @page { size: ${pageSize}; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { width: 100%; height: 100%; }
  .wrap { width: 100vw; height: 100vh; display: flex; align-items: center; justify-content: center; }
  img { width: 100%; height: 100%; object-fit: contain; display: block; }
</style></head><body><div class="wrap"><img id="pimg" src="${imageDataUrl}" alt="label" /></div></body></html>`);
      doc.close();

      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print failed', e);
          setError('Your browser blocked the print dialog. Click Download instead and print the PNG from your file manager.');
        }
      };
      const img = doc.getElementById('pimg');
      if (img && img.complete && img.naturalWidth > 0) setTimeout(doPrint, 250);
      else if (img) img.addEventListener('load', () => setTimeout(doPrint, 250));
      else setTimeout(doPrint, 400);

      // Clean up the iframe shortly after
      setTimeout(() => { try { iframe.remove(); } catch {} }, 15000);
    } catch (e) {
      console.error('Print iframe error', e);
      setError('Could not open print dialog. Use Download instead.');
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setParseResult(null); setItems([]); setSelected({}); setMapping({}); setSkuMsg(''); setCommittedCount(null); setError('');
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedItems = items.filter(it => selected[it.row_id]);
  const totals = selectedItems.reduce((acc, it) => {
    const qty = Number(it.quantity) || 1;
    acc.cost += (Number(it.cost) || 0) * qty;
    acc.retail += (Number(it.retail_price) || 0) * qty;
    acc.selling += (Number(it.final_price) || 0) * qty;
    return acc;
  }, { cost: 0, retail: 0, selling: 0 });
  const profit = totals.selling - totals.cost;
  const marginPct = totals.selling > 0 ? (profit / totals.selling) * 100 : 0;
  const feesPct = 3; // eBay/Stripe default
  const netAfterFees = profit - (totals.selling * feesPct / 100);

  if (committedCount !== null) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#0F172A]">{committedCount} products saved</h2>
        <p className="text-sm text-slate-500 mt-2">They're now in your Products list. You can print labels or push to any integration from there.</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <a href="/product-list" className="px-5 py-2.5 rounded-lg bg-[#0052FF] text-white text-sm font-semibold hover:bg-[#0040CC]" data-testid="bulk-goto-list">View Products</a>
          <button onClick={reset} className="px-5 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200" data-testid="bulk-upload-another">Upload another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-upload-page">
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Bulk Upload</h2>
        <p className="text-sm text-slate-500 mt-1">Import a target manifest, Walmart invoice, or any CSV/Excel file with UPC + product name.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 text-xs">
        {['Upload file', 'Map columns', 'Review & save'].map((label, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${step === i + 1 ? 'bg-[#0052FF] text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className="font-bold">{i + 1}</span>
              <span>{label}</span>
            </div>
            {i < 2 && <ArrowRight className="w-3 h-3 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="bulk-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {enrichProgress && enrichProgress.status === 'running' && (
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="enrich-progress">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-[#0052FF] animate-spin flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-semibold text-[#0F172A]">Enriching products…</span>
                <span className="font-mono text-slate-600">{enrichProgress.done} / {enrichProgress.total}</span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#0052FF] transition-all duration-300"
                  style={{ width: `${enrichProgress.total ? Math.round((enrichProgress.done / enrichProgress.total) * 100) : 0}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Rows appear in the table below as they finish. You can edit / push / print enriched rows while the rest load.</p>
            </div>
          </div>
        </div>
      )}
      {enrichProgress && enrichProgress.status === 'done' && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 flex items-center gap-2" data-testid="enrich-progress-done">
          <CheckCircle2 className="w-4 h-4" />
          <span>Enrichment complete — {enrichProgress.done} of {enrichProgress.total} rows processed.</span>
        </div>
      )}

      {bulkSummary && (
        <div
          className={`p-4 rounded-lg border text-sm ${bulkSummary.failed.length === 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
          data-testid="bulk-summary"
        >
          <div className="flex items-start gap-3">
            {bulkSummary.failed.length === 0
              ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" />
              : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
            }
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base">
                {bulkSummary.ok} of {bulkSummary.total} pushed to {bulkSummary.platform === 'shopify' ? 'Shopify' : 'Square'}
                {bulkSummary.failed.length > 0 && <span className="ml-2 text-amber-800">· {bulkSummary.failed.length} failed</span>}
              </div>
              {bulkSummary.needsReconnect && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs">Shopify OAuth is missing the <code className="px-1 py-0.5 bg-white/60 rounded font-mono">read_publications</code> scope — products won't appear on any sales channel until you reconnect.</span>
                  <a
                    href="/configuration?reconnect=shopify"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#95BF47] hover:bg-[#7AAB3C] text-white text-xs font-bold"
                    data-testid="bulk-summary-reconnect-shopify"
                  >
                    <ShoppingBag className="w-3.5 h-3.5" /> Reconnect Shopify
                  </a>
                </div>
              )}
              {bulkSummary.failed.length > 0 && !bulkSummary.needsReconnect && (
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-2">
                  {bulkSummary.failed.map((f, i) => (
                    <li key={i} className="text-xs flex items-start gap-1.5" data-testid={`bulk-summary-failed-${i}`}>
                      <X className="w-3 h-3 mt-0.5 flex-shrink-0 text-red-500" />
                      <span><strong className="font-semibold">{f.name}</strong> — {f.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={() => setBulkSummary(null)}
              className="text-current/60 hover:text-current p-1"
              aria-label="dismiss"
              data-testid="bulk-summary-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Label preview modal — shown after a Quick Print. Click Print inside this dialog (direct user click) reliably triggers print dialog. */}
      {labelPreview && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLabelPreview(null)} data-testid="bulk-label-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#0052FF]">Label ready</p>
                <p className="text-sm font-semibold text-[#0F172A] truncate max-w-xs">{labelPreview.product_name}</p>
              </div>
              <button onClick={() => setLabelPreview(null)} className="text-slate-400 hover:text-slate-600 p-1" data-testid="bulk-label-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 bg-slate-50 flex items-center justify-center">
              <img src={labelPreview.image} alt="label" className="max-w-full max-h-[50vh] border border-slate-200 bg-white shadow-sm" />
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-white">
              <button
                onClick={() => openPrintWindow(labelPreview.image, `Label · ${labelPreview.product_name}`, labelPreview.size)}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg py-2.5 text-sm"
                data-testid="bulk-label-print-btn">
                <Printer className="w-4 h-4" /> Print {labelPreview.size ? `(${labelPreview.size})` : ''}
              </button>
              <a href={labelPreview.image} download={`label-${labelPreview.product_name.replace(/\s+/g, '-').slice(0, 30)}.png`}
                className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg py-2.5 px-4 text-sm"
                data-testid="bulk-label-download-btn">
                Download
              </a>
              <button onClick={() => setLabelPreview(null)}
                className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg py-2.5 px-4 text-sm"
                data-testid="bulk-label-close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 1: Upload */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="bulk-step-1">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); uploadFile(f); } }} data-testid="bulk-file-input" />
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#0052FF]/10 mb-4">
            <FileSpreadsheet className="w-7 h-7 text-[#0052FF]" />
          </div>
          <h3 className="font-bold text-[#0F172A] mb-1">Select your manifest file</h3>
          <p className="text-xs text-slate-500 mb-5">CSV or Excel · Minimum columns: UPC/Barcode + Product Name</p>
          <button onClick={() => fileRef.current?.click()} disabled={parsing}
            className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
            data-testid="bulk-upload-button">
            {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {parsing ? 'Parsing...' : 'Choose File'}
          </button>
        </div>
      )}

      {/* STEP 2: Column mapping */}
      {step === 2 && parseResult && (
        <div className="space-y-4" data-testid="bulk-step-2">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className="w-5 h-5 text-[#0052FF]" />
              <p className="text-sm font-semibold text-[#0F172A]">{parseResult.filename}</p>
              <span className="text-xs text-slate-500">· {parseResult.total_rows} rows</span>
              <button onClick={reset} className="ml-auto text-slate-400 hover:text-red-500" data-testid="bulk-cancel-step-2"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs font-semibold text-slate-600 mb-3">Map your file's columns to our fields. We've auto-detected what we could — adjust any that look wrong.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.keys(FIELD_LABELS).map(field => (
                <div key={field}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{FIELD_LABELS[field]}</label>
                  <select value={mapping[field] || ''} onChange={e => setMapping(prev => ({ ...prev, [field]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid={`map-${field}`}>
                    <option value="">— Not in file —</option>
                    {parseResult.headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Assign to batch (optional)</label>
              <select value={batchId} onChange={e => setBatchId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="bulk-batch-select">
                <option value="">— No batch —</option>
                {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name} ({b.batch_id})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Default cost % (of retail)</label>
              <div className="relative">
                <input type="number" min="0" max="100" value={costPercent} onChange={e => setCostPercent(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm pr-7 focus:border-[#0052FF] outline-none" data-testid="bulk-cost-percent" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">%</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1" title="If your manifest shows the total for all units on each line (e.g. Sam's Club 'Liquidation price'), pick Line Total — we'll divide by Qty to get per-unit cost.">Cost column is</label>
              <select value={costBasis} onChange={e => setCostBasis(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="bulk-cost-basis">
                <option value="auto">Auto-detect</option>
                <option value="per_item">Per item</option>
                <option value="line_total">Line total (÷ by Qty)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Retail column is</label>
              <select value={retailBasis} onChange={e => setRetailBasis(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="bulk-retail-basis">
                <option value="auto">Auto-detect</option>
                <option value="per_item">Per item</option>
                <option value="line_total">Line total (÷ by Qty)</option>
              </select>
            </div>
          </div>
          {/* Sample preview */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 text-xs font-semibold text-slate-600">Sample rows from your file</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>{parseResult.headers.map(h => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(parseResult.sample_rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">{parseResult.headers.map(h => <td key={h} className="px-3 py-2 text-slate-700">{r[h]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <button onClick={processRows} disabled={processing}
            className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
            data-testid="bulk-process-btn">
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {processing ? 'Enriching rows (1 API call per row)...' : 'Continue to review'}
          </button>
        </div>
      )}

      {/* STEP 3: Review */}
      {step === 3 && (
        <div className="space-y-4" data-testid="bulk-step-3">
          {skuMsg && <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">{skuMsg}</div>}

          {/* Bulk Pricing + Profit Summary */}
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Bulk pricing bar */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="bulk-pricing-bar">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulk Pricing</span>
                <span className="text-xs text-slate-400">· applies to selected rows</span>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Pricing rule</label>
                  <select value={priceMode} onChange={e => setPriceMode(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="bulk-price-mode">
                    <option value="markup_cost">Markup % from Cost</option>
                    <option value="discount_retail">Discount % from Retail</option>
                    <option value="flat_margin">Target Margin % on Sale</option>
                  </select>
                </div>
                <div className="w-24">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Percent</label>
                  <div className="relative">
                    <input type="number" min="0" step="0.1" value={pricePct}
                      onChange={e => setPricePct(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm pr-7 focus:border-[#0052FF] outline-none"
                      data-testid="bulk-price-pct" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">%</span>
                  </div>
                </div>
                <div className="w-28">
                  <label className="block text-xs font-semibold text-slate-600 mb-1" title="Label size used when you click the Print icon on a row">Label size</label>
                  <select value={printTemplateId} onChange={e => setPrintTemplateId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#0052FF] outline-none" data-testid="bulk-label-size">
                    {labelTemplates.map(t => <option key={t.id} value={t.id}>{t.size || t.name}</option>)}
                  </select>
                </div>
                <button onClick={applyBulkPricing} disabled={selectedCount === 0}
                  className="px-4 py-2 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="bulk-price-apply">Apply to {selectedCount}</button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {priceMode === 'markup_cost' && 'Final = Cost × (1 + %) — e.g. 30% markup on $10 cost → $13'}
                {priceMode === 'discount_retail' && 'Final = Retail × (1 − %) — e.g. 20% off $100 retail → $80'}
                {priceMode === 'flat_margin' && 'Final = Cost ÷ (1 − %) — guarantees the chosen margin on the sale price'}
              </p>
            </div>

            {/* Profit summary card */}
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-4 text-white" data-testid="bulk-profit-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Profit Summary</span>
                <span className="text-[10px] text-slate-400">{selectedCount} items</span>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-400">Total Cost</span><span className="font-semibold tabular-nums">${totals.cost.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Total Retail</span><span className="font-semibold tabular-nums">${totals.retail.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Total Selling</span><span className="font-semibold tabular-nums">${totals.selling.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 border-t border-slate-700">
                  <span className="text-green-300 font-bold">Gross Profit</span>
                  <span className={`font-bold tabular-nums ${profit >= 0 ? 'text-green-300' : 'text-red-300'}`}>${profit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Margin</span>
                  <span className={`font-semibold tabular-nums ${marginPct >= 0 ? 'text-green-300' : 'text-red-300'}`}>{marginPct.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="text-slate-400" title="After ~3% marketplace/payment fees">Net (after 3% fees)</span>
                  <span className={`font-semibold tabular-nums ${netAfterFees >= 0 ? 'text-green-300' : 'text-red-300'}`}>${netAfterFees.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-slate-600"><strong>{selectedCount}</strong> of {items.length} selected
              {autoSave.total > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold" data-testid="bulk-autosave-status">
                  {autoSave.running
                    ? <><Loader2 className="w-3 h-3 animate-spin text-[#0052FF]" /><span className="text-[#0052FF]">Auto-saving {autoSave.saved}/{autoSave.total} to Products…</span></>
                    : <><CheckCircle2 className="w-3 h-3 text-green-600" /><span className="text-green-700">{autoSave.saved} saved to Products</span></>}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1" data-testid="margin-range">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Cost %</span>
                <input type="number" min="0" max="1000" value={marginMin} onChange={e => setMarginMin(e.target.value)}
                  className="w-12 px-1.5 py-0.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]"
                  data-testid="bulk-margin-min" />
                <span className="text-xs text-slate-400">–</span>
                <input type="number" min="0" max="1000" value={marginMax} onChange={e => setMarginMax(e.target.value)}
                  className="w-12 px-1.5 py-0.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]"
                  data-testid="bulk-margin-max" />
                <span className="text-xs text-slate-400">%</span>
                {availableCategories.length > 0 && (
                  <>
                    <span className="text-[11px] text-slate-300">·</span>
                    <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="max-w-[160px] px-1.5 py-0.5 rounded border border-slate-200 text-xs outline-none focus:border-[#0052FF]" data-testid="bulk-category-filter">
                      <option value="">All categories</option>
                      {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </>
                )}
                <button onClick={selectByMargin} className="text-xs px-2 py-0.5 rounded bg-[#0F172A] text-white hover:bg-slate-800 font-semibold" data-testid="bulk-select-by-margin">Select</button>
              </div>
              <button onClick={() => setSelected(Object.fromEntries(items.map(it => [it.row_id, true])))}
                className="text-xs text-[#0052FF] hover:underline font-semibold" data-testid="bulk-select-all">Select all</button>
              <button onClick={() => setSelected({})}
                className="text-xs text-slate-500 hover:underline" data-testid="bulk-select-none">Clear</button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          <div className="bg-white border-2 border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="bulk-actions-bar">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-2">Bulk actions on {selectedCount}:</span>
            {/* Secondary Shopify Collection picker — applied to every row pushed below.
                Primary collection (auto-created from manifest category) is always attached. */}
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200" title="Every Shopify push will also be added to this collection (in addition to the manifest-category collection).">
              <ShoppingBag className="w-3.5 h-3.5 text-emerald-700" />
              <select value={secondaryCollection} onChange={e => setSecondaryCollection(e.target.value)}
                className="bg-transparent text-xs font-semibold text-emerald-900 focus:outline-none max-w-[200px]"
                data-testid="bulk-secondary-collection">
                <option value="">+ Secondary collection (none)</option>
                {loadingShopifyColl && <option disabled>Loading…</option>}
                {shopifyCollections.map(c => (
                  <option key={c.id} value={c.id}>{c.title}{c.products_count ? ` (${c.products_count})` : ''}</option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer"
              title="Shopify Physical product toggle. ON = requires shipping. OFF = digital/service.">
              <input type="checkbox" checked={batchRequiresShipping}
                onChange={e => setBatchRequiresShipping(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                data-testid="bulk-physical-toggle" />
              <span className="text-xs font-semibold text-blue-900">{batchRequiresShipping ? 'Physical' : 'Digital'}</span>
            </label>
            <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer"
              title="Local Pickup Only: pushes as PHYSICAL + taxable (POS charges tax) but assigns a no-shipping-rates profile so online checkout only offers store pickup. Per-row override via the pin icon in Actions.">
              <input type="checkbox" checked={batchLocalPickup}
                onChange={e => setBatchLocalPickup(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                data-testid="bulk-local-pickup-toggle" />
              <MapPin className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-xs font-semibold text-amber-900">Pickup only</span>
            </label>
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-violet-50 border border-violet-200"
              title="Shopify sales channels for every push in this batch: publish to ALL channels (Online Store, POS, etc.) or to Point of Sale ONLY (product won't appear on your online store).">
              <Store className="w-3.5 h-3.5 text-violet-700" />
              <select value={batchChannelMode} onChange={e => setBatchChannelMode(e.target.value)}
                className="bg-transparent text-xs font-semibold text-violet-900 focus:outline-none"
                data-testid="bulk-channel-mode">
                <option value="all">All channels</option>
                <option value="pos">POS only</option>
              </select>
            </div>
            <button onClick={() => bulkPrint()} disabled={!!bulkBusy || selectedCount === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0F172A] hover:bg-black text-white text-xs font-semibold disabled:opacity-40"
              data-testid="bulk-action-print">
              {bulkBusy === 'print' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Print tags (by qty)
            </button>
            <button onClick={() => bulkPush('shopify')} disabled={!!bulkBusy || selectedCount === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#95BF47] hover:bg-[#7AAB3C] text-white text-xs font-semibold disabled:opacity-40"
              data-testid="bulk-action-shopify">
              {bulkBusy === 'shopify' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingBag className="w-3.5 h-3.5" />} Push to Shopify
            </button>
            <button onClick={() => bulkPush('square')} disabled={!!bulkBusy || selectedCount === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-40"
              data-testid="bulk-action-square">
              {bulkBusy === 'square' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Store className="w-3.5 h-3.5" />} Push to Square
            </button>
            {bulkBusy && bulkProgress.total > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <div className="h-1.5 w-40 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#0052FF] transition-all" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} />
                </div>
                <span className="text-xs font-semibold text-slate-600 tabular-nums">{bulkProgress.done} / {bulkProgress.total}</span>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="w-14 px-3 py-2 text-left">Image</th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="w-16 px-3 py-2 text-left">Qty</th>
                    <th className="w-20 px-3 py-2 text-left">Retail $</th>
                    <th className="w-20 px-3 py-2 text-left">Cost $</th>
                    <th className="w-28 px-3 py-2 text-left">Final $ / Resale</th>
                    <th className="w-20 px-3 py-2 text-left">Profit</th>
                    <th className="w-40 px-3 py-2 text-left">Actions</th>
                    <th className="w-10 px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const qty = Number(it.quantity) || 1;
                    const cost = Number(it.cost) || 0;
                    const retail = Number(it.retail_price) || 0;
                    const final_ = Number(it.final_price) || 0;
                    const rowProfit = (final_ - cost) * qty;
                    // Resale % = what portion of Retail the Final is (e.g. 35% off retail → 65% resale)
                    const rowResalePct = retail > 0 ? (final_ / retail) * 100 : 0;
                    // Keep margin handy for color coding (still a good profit indicator)
                    const rowMargin = final_ > 0 ? ((final_ - cost) / final_) * 100 : 0;
                    return (
                    <tr key={it.row_id} className="border-t border-slate-100" data-testid={`bulk-row-${it.row_id}`}>
                      <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!selected[it.row_id]} onChange={e => setSelected(prev => ({ ...prev, [it.row_id]: e.target.checked }))} data-testid={`bulk-check-${it.row_id}`} /></td>
                      <td className="px-3 py-2">
                        {it.images?.[0] ? <img src={it.images[0]} alt="" className="w-10 h-10 object-contain rounded border bg-white" /> : <div className="w-10 h-10 rounded bg-slate-100" />}
                      </td>
                      <td className="px-3 py-2">
                        <input value={it.product_name} onChange={e => updateItem(it.row_id, 'product_name', e.target.value)} onBlur={() => syncRowEdit(it.row_id)} className="w-full text-sm border-0 bg-transparent outline-none focus:bg-slate-50 px-1 py-0.5 rounded" />
                        <div className="flex items-center gap-2 text-xs mt-0.5 flex-wrap">
                          <span className="text-slate-400 font-mono">{it.barcode}</span>
                          {it.item_number && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-semibold font-mono" data-testid={`bulk-itemnum-${it.row_id}`}>
                              Item #{it.item_number}
                            </span>
                          )}
                          {it.category && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold" data-testid={`bulk-category-${it.row_id}`}>{it.category}</span>}
                          {retail > 0 && cost > 0 && (
                            <span className="text-[10px] text-slate-500" data-testid={`bulk-costpct-${it.row_id}`}>
                              Paid: <strong className="text-[#0F172A]">{Math.round((cost / retail) * 100)}%</strong> of retail
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2"><input type="number" min="1" value={it.quantity} onChange={e => updateItem(it.row_id, 'quantity', Number(e.target.value) || 1)} onBlur={() => syncRowEdit(it.row_id)} className="w-14 border border-slate-200 rounded px-2 py-1 text-xs" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" value={it.retail_price} onChange={e => updateItem(it.row_id, 'retail_price', Number(e.target.value) || 0)} onBlur={() => syncRowEdit(it.row_id)} className="w-20 border border-slate-200 rounded px-2 py-1 text-xs" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" value={it.cost} onChange={e => updateItem(it.row_id, 'cost', Number(e.target.value) || 0)} className="w-20 border border-slate-200 rounded px-2 py-1 text-xs" /></td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.01" value={it.final_price} onChange={e => updateItem(it.row_id, 'final_price', Number(e.target.value) || 0)} onBlur={() => syncRowEdit(it.row_id)} className="w-20 border border-slate-200 rounded px-2 py-1 text-xs" data-testid={`bulk-final-${it.row_id}`} />
                        <div className="mt-1 flex items-center gap-1">
                          <input
                            type="number" step="1" min="0" max="1000"
                            value={Math.round(rowResalePct)}
                            onChange={e => {
                              const pct = Number(e.target.value);
                              if (isNaN(pct) || retail <= 0) return;
                              // Resale % = Final / Retail × 100  →  Final = Retail × (pct / 100)
                              const newPrice = retail * (pct / 100);
                              updateItem(it.row_id, 'final_price', Math.round(newPrice * 100) / 100);
                            }}
                            onBlur={() => syncRowEdit(it.row_id)}
                            className={`w-12 px-1 py-0.5 rounded border border-slate-200 text-[10px] font-bold tabular-nums outline-none focus:border-[#0052FF] ${rowMargin >= 20 ? 'text-green-600' : rowMargin >= 0 ? 'text-amber-600' : 'text-red-600'}`}
                            title="Resale % of retail — e.g. 65 means 35% off retail. Edit to auto-adjust Sale Price."
                            data-testid={`bulk-margin-${it.row_id}`}
                          />
                          <span className="text-[10px] font-semibold text-slate-400">% resale</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-bold tabular-nums ${rowProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${rowProfit.toFixed(2)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button onClick={() => quickPrint(it)} disabled={!!rowAction[it.row_id]}
                            className="p-1.5 rounded-md bg-slate-100 hover:bg-[#0052FF] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                            title="Save + Print label" data-testid={`quick-print-${it.row_id}`}>
                            {rowAction[it.row_id] === 'print' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => quickPushSquare(it)} disabled={!!rowAction[it.row_id]}
                            className="p-1.5 rounded-md bg-slate-100 hover:bg-black hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                            title={it.images?.[0] ? 'Save + Push to Square' : 'Save + Push to Square POS (no image → POS only)'} data-testid={`quick-square-${it.row_id}`}>
                            {rowAction[it.row_id] === 'square' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Store className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => quickPushShopify(it)} disabled={!!rowAction[it.row_id]}
                            className="p-1.5 rounded-md bg-slate-100 hover:bg-[#95BF47] hover:text-white text-slate-600 disabled:opacity-50 transition-colors"
                            title={it.images?.[0] ? 'Save + Push to Shopify' : 'Save + Push to Shopify POS (no image → POS only, not Online Store)'} data-testid={`quick-shopify-${it.row_id}`}>
                            {rowAction[it.row_id] === 'shopify' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingBag className="w-3.5 h-3.5" />}
                          </button>
                          {(() => {
                            const pk = typeof it.localPickupOnly === 'boolean' ? it.localPickupOnly : batchLocalPickup;
                            return (
                              <button onClick={() => updateItem(it.row_id, 'localPickupOnly', !pk)}
                                className={`p-1.5 rounded-md transition-colors ${pk ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-100 text-slate-400 hover:bg-amber-100 hover:text-amber-700'}`}
                                title={pk ? 'Local Pickup Only: ON — physical + taxable, checkout offers store pickup only' : 'Local Pickup Only: OFF — ships normally (click to make pickup-only)'}
                                data-testid={`quick-pickup-${it.row_id}`}>
                                <MapPin className="w-3.5 h-3.5" />
                              </button>
                            );
                          })()}
                        </div>
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          {rowStatus[it.row_id]?.ok === true && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300 font-semibold inline-flex items-center gap-0.5" data-testid={`row-status-ok-${it.row_id}`}>
                              <CheckCircle2 className="w-3 h-3" /> pushed
                            </span>
                          )}
                          {rowStatus[it.row_id]?.warn && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300 font-semibold max-w-[180px] truncate"
                              title={rowStatus[it.row_id].warn} data-testid={`row-status-warn-${it.row_id}`}>
                              {rowStatus[it.row_id].warn}
                            </span>
                          )}
                          {rowStatus[it.row_id]?.ok === false && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-300 font-semibold inline-flex items-center gap-0.5 max-w-[180px] truncate"
                              title={rowStatus[it.row_id].reason}
                              data-testid={`row-status-fail-${it.row_id}`}
                            >
                              <AlertCircle className="w-3 h-3 flex-shrink-0" /> {rowStatus[it.row_id].reason}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => dropRow(it.row_id)} className="text-slate-300 hover:text-red-500" data-testid={`bulk-drop-${it.row_id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-slate-500 hover:text-slate-700">&larr; Back to mapping</button>
            <button onClick={commit} disabled={committing}
              className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
              data-testid="bulk-commit-btn">
              {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {committing ? 'Finishing…' : 'Done — View Products'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
